import React, { useEffect, useRef } from 'react';
import { PageHeader } from '../../components';
import { Card, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Loading } from '../../components';
import { connect } from 'react-redux';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import useComponentState from '../../utils/useComponentState';
import { getDenominationsSummary } from '../../api/merchantVoucher';

import styles from './DenominationsSummary.module.scss';

const DenominationsSummary = (props) => {
  const [state, setState] = useComponentState({
    stores: [{ key: 'ALL', label: 'All' }],
    selectedStoreKey: 'ALL',
    tableHeading: {
      column: ['ID', 'Merchant', 'Denomination', 'Quantity'],
    },
    loading: false,
    fetchedData: [],
    tableData: [],
    fetchError: '',
  });
  const {
    stores,
    selectedStoreKey,
    loading,
    tableData,
    fetchError,
    tableHeading,
    notFoundMessage,
    fetchedData,
  } = state;

  useEffect(() => {
    getDenominations();
  }, []);

  const sortByIdAsc = (rows) => {
    if (!Array.isArray(rows)) return rows;
    return [...rows].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  };

  const sortByDenominationAsc = (rows) => {
    if (!Array.isArray(rows)) return rows;
    return [...rows].sort(
      (a, b) => (Number(a.denomination) || 0) - (Number(b.denomination) || 0)
    );
  };

  const handleOnBlockChange = (key) => {
    setState({ selectedStoreKey: key });
    if (key === 'ALL') {
      getDenominations();
    } else {
      const filteredData = fetchedData.filter((data) => {
        return data.merchantVoucherId === key;
      });
      setState({ tableData: sortByDenominationAsc(filteredData) });
    }
  };

  const storeOptions = stores.map(({ key, label }) => ({
    value: key,
    label,
  }));
  const selectedStoreOption =
    storeOptions.find((option) => option.value === selectedStoreKey) || null;

  const handleStoreSearchChange = (option) => {
    handleOnBlockChange(option ? option.value : 'ALL');
  };

  const tabStripRef = useRef(null);
  const scrollTabs = (direction) => {
    if (tabStripRef.current) {
      tabStripRef.current.scrollBy({ left: direction * 220, behavior: 'smooth' });
    }
  };

  // Whenever the selected store changes — from clicking a tab or picking
  // one in the search dropdown — bring that tab into view in the strip.
  useEffect(() => {
    if (!tabStripRef.current) return;
    const activeTab = tabStripRef.current.querySelector(`[id="${selectedStoreKey}"]`);
    if (activeTab) {
      activeTab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedStoreKey]);

  const appendStores = (response) => {
    let fetchedStores = stores;
    response.forEach((denomination) => {
      const { merchantVoucherId, merchantVoucherName } = denomination;
      if (!stores.some((store) => store.key === merchantVoucherId)) {
        fetchedStores.push({
          key: merchantVoucherId,
          label: merchantVoucherName,
        });
      }
    });
    return fetchedStores;
  };

  const getDenominations = async () => {
    try {
      setState({ loading: true });
      const token = props.auth.auth.token;
      const response = await getDenominationsSummary(token);
      const fetchedStores = appendStores(response);
      setState({
        stores: fetchedStores,
        fetchedData: response,
        tableData: sortByIdAsc(response),
        loading: false,
        notFoundMessage:
          !response || response.length < 1 ? 'No denominations found' : '',
      });
    } catch (err) {
      setState({
        fetchError: 'Failed to fetch purchases',
        loading: false,
        tableData: [],
      });
    }
  };

  const getToFixed = (amount) => {
    return amount
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const renderTableData = () => {
    return (
      <Card className={styles.table_div}>
        <div>
          <Table responsive hover>
            <thead className={styles.table_head}>
              <tr>
                {tableHeading.column.map((item, index) => {
                  return <th key={index}>{item}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {notFoundMessage ? (
                <tr>
                  <td colSpan={6} className='text-center'>
                    {notFoundMessage}
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={6} className='text-center'>
                    <Loading text='Loading' />
                  </td>
                </tr>
              ) : (
                tableData.map((data) => {
                  return (
                    <tr
                      key={data.id}
                      className={styles.table_row}
                      /* onClick={() => {
                        this.handleOnClick(data);
                      }} */
                    >
                      <td>{data.id}</td>
                      <td>{data.merchantVoucherName}</td>
                      <td>{getToFixed(data.denomination)}</td>
                      <td>{data.availableQuantity}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    );
  };

  const renderStoreBlocks = () => {
    return stores.map((item) => {
      const { key, label } = item;
      return (
        <OverlayTrigger
          key={key}
          placement='top'
          overlay={<Tooltip id={`store-tab-tooltip-${key}`}>{label}</Tooltip>}
        >
          <Card
            className={
              selectedStoreKey !== key
                ? styles.filter_card
                : styles.filter_card_active
            }
            id={key}
            onClick={() => {
              handleOnBlockChange(key);
            }}
          >
            <Card.Body>
              <Card.Text>{label}</Card.Text>
            </Card.Body>
          </Card>
        </OverlayTrigger>
      );
    });
  };
  return (
    <div className={styles.root__div}>
      <div className={styles.root__div_main}>
        <PageHeader text='Stock Summary' />
        <div className={styles.store_search}>
          <Select
            classNamePrefix='store_search_select'
            options={storeOptions}
            value={selectedStoreOption}
            onChange={handleStoreSearchChange}
            placeholder='Search merchant...'
            isClearable
          />
        </div>
        <div className={styles.filter_block_wrapper}>
          <button
            type='button'
            className={styles.filter_scroll_btn}
            onClick={() => scrollTabs(-1)}
            aria-label='Scroll tabs left'
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className={styles.filter_block} ref={tabStripRef}>
            {renderStoreBlocks()}
          </div>
          <button
            type='button'
            className={styles.filter_scroll_btn}
            onClick={() => scrollTabs(1)}
            aria-label='Scroll tabs right'
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
        {renderTableData()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(DenominationsSummary);
